import type { ImportJobErrorFields } from "helpers/importJobError";
import { useEffect, useRef, useState } from "react";
import type { ImportJobStep, Job } from "store/jobsStore";
import { fetchJob, ProcessingJobStatus } from "store/jobsStore";
import { useAppDispatch } from "store/store";

const EMPTY_JOB_ERROR: ImportJobErrorFields = {};

/** Stored progress may be `45` or a legacy `45 %`. The UI appends `%` itself. */
function displayProgress(progress: string | null | undefined): string | null {
    if (progress == null) return null;
    const numeric = String(progress).replace(/%/g, "").trim();
    return numeric === "" ? null : numeric;
}

const TERMINAL_JOB_STATUSES = new Set([
    ProcessingJobStatus.DONE,
    ProcessingJobStatus.FAILED,
    ProcessingJobStatus.REVERTED,
]);

interface UseImportJobStatusParams {
    jobId?: number;
    pollingInterval?: number;
    onStatusUpdate?: (data: { status: ProcessingJobStatus; step: ImportJobStep; jobId: number }) => void;
}

interface UseImportJobStatusReturn {
    jobStatus: ProcessingJobStatus | null;
    jobStep: ImportJobStep | undefined;
    progress: string | null;
    isPolling: boolean;
    error: string | null;
    jobError: ImportJobErrorFields;
}

export function useImportJobStatus({
    jobId,
    pollingInterval = 6000,
    onStatusUpdate,
}: UseImportJobStatusParams = {}): UseImportJobStatusReturn {
    const [jobStatus, setJobStatus] = useState<ProcessingJobStatus | null>(null);
    const [jobStep, setJobStep] = useState<ImportJobStep | undefined>(undefined);
    const [progress, setProgress] = useState<string | null>(null);
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [jobError, setJobError] = useState<ImportJobErrorFields>(EMPTY_JOB_ERROR);

    const dispatch = useAppDispatch();
    const intervalRef = useRef<NodeJS.Timeout | number | null>(null);

    const isPending = useRef(false);

    useEffect(() => {
        if (jobId) {
            setIsPolling(true);
            setError(null);

            const checkJobStatus = () => {
                if (isPending.current) return;

                isPending.current = true;

                return dispatch(fetchJob({ jobId }))
                    .then(({ payload, meta }) => {
                        if (meta.requestStatus === "fulfilled") {
                            const job = payload as Job;
                            setJobStatus(job.status);
                            setJobStep(job.metadata?.step);
                            setJobError({
                                errorCode: job.metadata?.errorCode,
                                errorMessage: job.metadata?.errorMessage,
                                errorParams: job.metadata?.errorParams,
                            });

                            if (job.status === ProcessingJobStatus.PROCESSING) {
                                setProgress(displayProgress(job.progress));
                            } else {
                                setProgress(null);
                            }

                            if (TERMINAL_JOB_STATUSES.has(job.status) && intervalRef.current) {
                                clearInterval(intervalRef.current);
                                intervalRef.current = null;
                                setIsPolling(false);
                            }

                            setError(null);
                        } else if (meta.requestStatus === "rejected") {
                            setError("Failed to fetch job status");
                        }
                    })
                    .catch(() => {
                        setError("Failed to fetch job status");
                    })
                    .finally(() => {
                        isPending.current = false;
                    });
            };

            intervalRef.current = setInterval(() => {
                checkJobStatus();
            }, pollingInterval);

            // Initial check
            checkJobStatus();
        } else {
            setIsPolling(false);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                setIsPolling(false);
            }
        };
    }, [dispatch, jobId, pollingInterval]);

    // Reset state when jobId changes
    useEffect(() => {
        setJobStatus(null);
        setJobStep(undefined);
        setProgress(null);
        setError(null);
        setJobError(EMPTY_JOB_ERROR);
    }, [jobId]);

    // Call onStatusUpdate callback when status or step changes
    useEffect(() => {
        if (jobStatus !== undefined && jobStatus !== null && jobStep && onStatusUpdate && jobId) {
            onStatusUpdate({ status: jobStatus, step: jobStep, jobId });
        }
    }, [jobStatus, jobStep, onStatusUpdate, jobId]);

    return {
        jobStatus,
        jobStep,
        progress,
        isPolling,
        error,
        jobError,
    };
}
